import i18nFactory from '@yandex-int/i18n';

import {
    FlattenRegions,
    Lang,
    Region,
    RegionInfo,
    RegionSelectedInfo
} from '../../typings/common';
import { GdGeotreeType } from '../../typings/graphql-grid';
import { WebpackRequire } from '../../typings/require';
import { Regions } from '../../typings/state';
import { captureChunkLoadException } from '../ErrorsReporting/ErrorsReporting';
import { LangUtils } from '../Lang/Lang';
import { TreeHelpers } from '../TreeHelpers/TreeHelpers';

import * as keyset from './RegionsHelpers.i18n';

const i18n = i18nFactory(keyset);

function fetchRegions(): Promise<Regions> {
    return new Promise<Regions>(
        (resolve, reject): void => {
            (require as WebpackRequire).ensure(
                ['./regionsDist'],
                function(require: WebpackRequire): void {
                    resolve(require('./regionsDist').default.default);
                },
                error => {
                    captureChunkLoadException(error, 'regionsDist');
                    reject(error);
                },
                'regionsDist'
            );
        }
    );
}

export class RegionsHelpers {
    private static regionsByIds: object;
    private static regionsPromise?: Promise<Regions>;

    static fetchRegions(): Promise<Regions> {
        if (!RegionsHelpers.regionsPromise) {
            RegionsHelpers.regionsPromise = fetchRegions();
        }

        return RegionsHelpers.regionsPromise;
    }
    /**
     *
     * Формирует строку из имен регионов, перечисленных через переданный сепаратор, по переданным id
     *
     * @param regionsHash
     * @param {String} geoIds id регионов через запятую, если id отрицательный регион попадет в секцию "(кроме: )"
     * @param {String} separator
     * @returns {String}
     */
    static getRegionsTexts(
        regionsHash: Regions,
        geoIds: Array<number>,
        separator?: string
    ): string {
        const regionsById =
            this.regionsByIds ||
            this.flattenRegions(
                new Map<number, Region>(),
                RegionsHelpers.getRegionsTree(
                    regionsHash,
                    LangUtils.getCurrentLang()
                )
            );
        const geoNames: Array<{ name: string; excepts: Array<string> }> = [];
        let regs = geoIds.reduce((result: Array<number>, item: number) => {
                if (Boolean(regionsById[Math.abs(item)])) {
                    result.push(item);
                }

                return result;
            }, []),
            firstReg;

        // если регионы не заданы или первым задан 0, формируем названия из псевдо региона "Все" см. flattenRegions
        if (regs.length === 0 || regs[0] === 0) {
            regs = regionsById[0].inner.slice(0);
        } else if (regs[0] < 0) {
            // если первый елемент отрицательный формируем строку "Все (кроме: ... )"
            regs.unshift(0);
        }

        firstReg = regs.shift();
        firstReg = firstReg === undefined ? '' : firstReg;
        geoNames.push({
            name: regionsById[firstReg].name,
            excepts: []
        });

        regs.reduce(
            (
                result: Array<{ name: string; excepts: Array<string> }>,
                region: number
            ) => {
                if (region >= 0) {
                    result.push({
                        name: regionsById[region].name,
                        excepts: []
                    });
                } else {
                    result[geoNames.length - 1].excepts.push(
                        regionsById[Math.abs(region)].name
                    );
                }

                return result;
            },
            geoNames
        );

        return RegionsHelpers.formatGeoNames(geoNames, separator);
    }

    static formatGeoNames(
        geoNames: Array<{ name: string; excepts: Array<string> }>,
        separator?: string
    ): string {
        separator = separator || ', ';

        return geoNames
            .map((geoName: { name: string; excepts: Array<string> }) => {
                if (geoName.excepts.length) {
                    return i18n('region-except', {
                        region: geoName.name,
                        except: geoName.excepts.join(separator)
                    });
                }

                return geoName.name;
            })
            .join(separator);
    }

    static convertSelectedRegionsInfoToNameFormat(
        listSelectedRegions: Array<RegionSelectedInfo>
    ): string {
        return RegionsHelpers.formatGeoNames(
            listSelectedRegions.map(selectedRegion => {
                return {
                    name: selectedRegion.region.name,
                    excepts: selectedRegion.excepts.map(item => item.name)
                };
            })
        );
    }

    /**
     * Метод получения информации о выбранных регионах и их минус регионах
     * @param {Region[]} nodes список нод
     * @param {Record<number, boolean>} selectedMap map выбранных значений дерева
     * @returns {Array<RegionSelectedInfo>}
     */
    static getSelectedRegionsInfo(
        nodes: Region[],
        selectedMap: Record<number, boolean>
    ): Array<RegionSelectedInfo> {
        let listSelectedRegions: Array<RegionSelectedInfo> = [];

        nodes.forEach(node => {
            if (selectedMap[node.id]) {
                const listFirstNotSelectedTreeNodes = TreeHelpers.getFirstEqualsTreeNodesList(
                    node,
                    selectedMap,
                    false
                );

                listSelectedRegions.push({
                    region: RegionsHelpers.getFromNodeRegionInfo(node),
                    excepts: listFirstNotSelectedTreeNodes.map(
                        notSelectedTreeNode =>
                            RegionsHelpers.getFromNodeRegionInfo(
                                notSelectedTreeNode
                            )
                    )
                });

                const listNextTrees = listFirstNotSelectedTreeNodes.reduce(
                    (acc: Region[], item) => {
                        if (item.inner) {
                            acc = acc.concat(item.inner);
                        }

                        return acc;
                    },
                    []
                );

                listSelectedRegions = listSelectedRegions.concat(
                    RegionsHelpers.getSelectedRegionsInfo(
                        listNextTrees,
                        selectedMap
                    )
                );
            } else {
                if (node.inner) {
                    listSelectedRegions = listSelectedRegions.concat(
                        RegionsHelpers.getSelectedRegionsInfo(
                            node.inner,
                            selectedMap
                        )
                    );
                }
            }
        });

        return listSelectedRegions;
    }

    /**
     * Метод получения выбранных значений дерева из плоской структуры регионов (пример: 225, -11158)
     * @param {Region[]} nodes ноды, для которых происходит поиск
     * @param {Record<number, boolean>} flatStructureRegionsMap плоская структура регионов(true: + регион, false: - регион)
     * @param {boolean} isParentSelected флаг, характеризующий выбран ли родитель региона
     * @returns {Record<number, boolean>} объект выбранных значений дерева региона
     */
    static getSelectedMapFromFlatStructureRegions(
        nodes: Region[],
        flatStructureRegionsMap: Record<number, boolean>,
        isParentSelected?: boolean
    ): Record<number, boolean> {
        let selectedMap: Record<number, boolean> = {};

        for (let index = 0; index < nodes.length; index++) {
            const nodeId = nodes[index].id;
            const childs = nodes[index].inner;
            let isSelected = false;

            if (
                flatStructureRegionsMap[nodeId] ||
                (flatStructureRegionsMap[nodeId] === undefined &&
                    isParentSelected)
            ) {
                selectedMap[nodeId] = true;
                isSelected = true;
            }

            if (childs) {
                selectedMap = {
                    ...selectedMap,
                    ...RegionsHelpers.getSelectedMapFromFlatStructureRegions(
                        childs,
                        flatStructureRegionsMap,
                        isSelected
                    )
                };
            }
        }

        return selectedMap;
    }

    static getSelectedMapFromRawRegionData(
        nodes: Region[],
        rawRegionIds: number[]
    ): Record<number, boolean> {
        return RegionsHelpers.getSelectedMapFromFlatStructureRegions(
            nodes,
            rawRegionIds.reduce((flatStructureRegions, regionId) => {
                if (regionId < 0) {
                    flatStructureRegions[-regionId] = false;
                } else {
                    flatStructureRegions[regionId] = true;
                }

                return flatStructureRegions;
            }, {})
        );
    }

    static getFromNodeRegionInfo(node: Region): RegionInfo {
        return {
            id: node.id,
            name: node.name
        };
    }

    static getRegionsTree(
        regionsHash: Regions,
        lang: Lang,
        translocalTreeType?: GdGeotreeType
    ): Array<Region> {
        let key;

        if (lang === Lang.uk) {
            key = 'ua';
        } else {
            key = lang;
        }

        const transLocalKey =
            key +
            (translocalTreeType === GdGeotreeType.RUSSIAN ? '_for_ru' : '');

        return regionsHash[key][transLocalKey] as Array<Region>;
    }

    static convertRegionsSelectedInfoToFlattenList(
        selectedInfoList: RegionSelectedInfo[]
    ): number[] {
        return selectedInfoList.reduce((acc: number[], selectedItem) => {
            acc.push(parseInt(selectedItem.region.id, 10));

            acc = acc.concat(
                selectedItem.excepts.map(
                    exceptItem => -parseInt(exceptItem.id, 10)
                )
            );

            return acc;
        }, []);
    }

    /**
     * Разворачивает дерево регионов в плоский хеш где ключами являются id регионов
     */
    static flattenRegions(
        result: {},
        region: Region | Array<Region>
    ): FlattenRegions {
        if (Array.isArray(region)) {
            if (!result[0]) {
                result[0] = {
                    name: i18n('all'),
                    id: 0,
                    inner: region.map((item: Region) => {
                        return item.id;
                    })
                };
            }

            return region.reduce(RegionsHelpers.flattenRegions, result);
        }

        result[region.id] = {
            name: region.name,
            inner:
                region.inner &&
                region.inner.map((innerRegion: Region) => {
                    return innerRegion.id;
                })
        };

        if (region.inner) {
            region.inner.reduce(RegionsHelpers.flattenRegions, result);
        }

        return result;
    }
}
